/**
 * Цветовые константы проекта
 * Централизованное хранение всех цветовых значений для использования в JS/TS
 * 
 * ВСЕ ЦВЕТА БРЕНДА ВЫЧИСЛЯЮТСЯ ОТ ОДНОЙ БАЗОВОЙ ПЕРЕМЕННОЙ BRAND_PRIMARY
 * Измените только BRAND_PRIMARY, и все остальные цвета обновятся автоматически
 * 
 * Для использования в CSS используйте Tailwind классы или CSS переменные из app.css
 * Для использования в JS/TS импортируйте из этого файла
 */

// ===== БАЗОВЫЙ ЦВЕТ БРЕНДА =====
// ИЗМЕНИТЕ ТОЛЬКО ЭТУ ПЕРЕМЕННУЮ, ЧТОБЫ ИЗМЕНИТЬ ВСЕ ЦВЕТА САЙТА
export const BRAND_PRIMARY = '#DC2626'

// ===== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ =====
/**
 * Конвертирует hex цвет в RGB
 * @param {string} hex - Hex цвет (например, '#DC2626')
 * @returns {object} - Объект с r, g, b значениями
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

/**
 * Конвертирует RGB в hex
 * @param {number} r - Красный канал (0-255)
 * @param {number} g - Зеленый канал (0-255)
 * @param {number} b - Синий канал (0-255)
 * @returns {string} - Hex цвет
 */
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const hex = Math.round(x).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')
}

/**
 * Осветляет цвет на указанный процент
 * @param {string} hex - Hex цвет
 * @param {number} percent - Процент осветления (0-100)
 * @returns {string} - Осветленный hex цвет
 */
function lightenColor(hex, percent) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  
  const factor = percent / 100
  const r = Math.min(255, rgb.r + (255 - rgb.r) * factor)
  const g = Math.min(255, rgb.g + (255 - rgb.g) * factor)
  const b = Math.min(255, rgb.b + (255 - rgb.b) * factor)
  
  return rgbToHex(r, g, b)
}

/**
 * Затемняет цвет на указанный процент
 * @param {string} hex - Hex цвет
 * @param {number} percent - Процент затемнения (0-100)
 * @returns {string} - Затемненный hex цвет
 */
function darkenColor(hex, percent) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  
  const factor = percent / 100
  const r = Math.max(0, rgb.r * (1 - factor))
  const g = Math.max(0, rgb.g * (1 - factor))
  const b = Math.max(0, rgb.b * (1 - factor))
  
  return rgbToHex(r, g, b)
}

/**
 * Создает RGBA строку из hex цвета и opacity
 * @param {string} hex - Hex цвет (например, '#DC2626')
 * @param {number} opacity - Opacity от 0 до 1
 * @returns {string} - RGBA строка
 */
export function hexToRgba(hex, opacity) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

// ===== BRAND COLORS (ВСЕ ВЫЧИСЛЯЕТСЯ ОТ BRAND_PRIMARY) =====
export const BRAND_COLORS = {
  PRIMARY: BRAND_PRIMARY,
  ACCENT: lightenColor(BRAND_PRIMARY, 15),      // Осветляем на 15% для акцента
  DARK: darkenColor(BRAND_PRIMARY, 15),         // Затемняем на 15% для градиентов
  DARKER: darkenColor(BRAND_PRIMARY, 35),       // Еще темнее для HERO градиента
  LIGHT: lightenColor(BRAND_PRIMARY, 75),       // Очень светлый для фонов
  
  // Градиенты (вычисляются автоматически)
  GRADIENT: {
    PRIMARY: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${darkenColor(BRAND_PRIMARY, 15)} 100%)`,
    HERO: `linear-gradient(135deg, ${darkenColor(BRAND_PRIMARY, 35)} 0%, ${darkenColor(BRAND_PRIMARY, 15)} 25%, ${BRAND_PRIMARY} 50%, ${darkenColor(BRAND_PRIMARY, 15)} 75%, ${darkenColor(BRAND_PRIMARY, 35)} 100%)`
  },
  
  // RGBA варианты для теней и эффектов (вычисляются автоматически)
  RGBA: {
    SHADOW_LIGHT: hexToRgba(BRAND_PRIMARY, 0.1),
    SHADOW_MEDIUM: hexToRgba(BRAND_PRIMARY, 0.25),
    SHADOW_STRONG: hexToRgba(BRAND_PRIMARY, 0.3),
    SHADOW_VERY_STRONG: hexToRgba(BRAND_PRIMARY, 0.35),
    BACKGROUND_LIGHT: hexToRgba(BRAND_PRIMARY, 0.05)
  }
}

// ===== UI COLORS =====
export const UI_COLORS = {
  ICON: '#444984',           // Цвет иконок
  ICON_DEFAULT: '#475569',   // Цвет иконок по умолчанию (slate-600)
  ERROR: '#E83737',          // Цвет ошибок
  SUCCESS: '#16A34A',        // Цвет успеха (зеленый)
  PLACEHOLDER: '#9C9FC0',    // Цвет placeholder
  SECONDARY: '#919AA7',      // Вторичный цвет
  LINK: '#8588AF',           // Цвет ссылок
  FOOTER_BG: '#F6F8FC'       // Фон футера
}

// ===== SLATE COLORS (для совместимости) =====
export const SLATE_COLORS = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A'
}

// ===== ICON COLORS (для использования в компонентах) =====
export const ICON_COLORS = {
  BRAND: BRAND_PRIMARY,              // #DC2626 - основной цвет бренда
  WHITE: '#FFFFFF',                  // Белый для иконок на темном фоне
  SLATE_400: SLATE_COLORS[400],     // #94A3B8 - серый для неактивных иконок
  SLATE_500: SLATE_COLORS[500],     // #64748B - серый для иконок
  SLATE_800: SLATE_COLORS[800],     // #1E293B - темно-серый для иконок
  SUCCESS: UI_COLORS.SUCCESS,        // #16A34A - зеленый для успеха
  ERROR: UI_COLORS.ERROR             // #E83737 - красный для ошибок
}


